import React, { createContext, useContext, useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

/**
 * 主题 Provider — 使用 .dark / .light class 策略（与 sayfi-frontend 一致）。
 * 默认暗色主题，手动切换后 localStorage 持久化。
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('theme')
    return (savedTheme as Theme) || 'dark'
  })

  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(theme)
    localStorage.setItem('theme', theme)
    // 同步 theme-color meta 以匹配浏览器工具栏/状态栏颜色
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) {
      const bgColor = getComputedStyle(root).getPropertyValue('--app-bg').trim()
      meta.setAttribute('content', bgColor)
    }
  }, [theme])

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'))
  }

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
}

/**
 * 使用主题 hook — 返回当前主题和切换函数。
 * 必须在 ThemeProvider 内使用。
 */
export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
